import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  gql,
  HttpLink,
  NormalizedCacheObject,
  split,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { WebSocketLink } from '@apollo/client/link/ws'
import { getMainDefinition } from '@apollo/client/utilities'
import { Preferences } from '@capacitor/preferences'
import { persistCacheSync } from 'apollo3-cache-persist'
import { useMemo } from 'react'
import { SubscriptionClient } from 'subscriptions-transport-ws'

import { config, constants } from '@/config'
import { getAccessToken } from '@/core/auth'
import { getAppLanguage } from '@/core/native/storage'
import { StorageWrapper } from '@/core/native/storage/storageWrapper'
// NOTE: '@/line' barrel は auth 系経由で当ファイル方向へ依存が向くため deep import する
import { getLineAccessToken } from '@/line/liff/accessToken'

import packageJson from '../../../package.json'
export * from './state'
import { cache } from './state'

let apolloClient: ApolloClient<NormalizedCacheObject>

const createLink = () => {
  const authLink = setContext(async (_, { headers }) => {
    const accessToken = await getAccessToken()
    const language = (await getAppLanguage()) ?? 'ja-JP'
    // LINE ミニアプリビルドのみ値が入る（それ以外は null でヘッダ自体を送らない）。
    // サーバーはこのヘッダを LINE サービスメッセージ token 発行・送信、
    // 退会時の連動アプリ権限取消に使う（無ければ素通り）。
    const lineAccessToken = await getLineAccessToken()
    const lineHeaders = lineAccessToken
      ? {
          [constants.httpHeaders.X_UTONIQ_LINE_ACCESS_TOKEN]: lineAccessToken,
        }
      : {}

    if (!accessToken)
      return {
        headers: {
          ...headers,
          'x-utoniq-application-key': config.applicationKey,
          'Apollo-Require-Preflight': 'true',
          'x-utoniq-application-language': language,
          ...lineHeaders,
        },
      }

    return {
      headers: {
        ...headers,
        'x-utoniq-application-language': language,
        authorization: `Bearer ${accessToken}`,
        'x-utoniq-application-key': config.applicationKey,
        'Apollo-Require-Preflight': 'true',
        ...lineHeaders,
      },
    }
  })

  const httpLink = new HttpLink({
    uri: config.coreApiUrl,
    fetch,
  })

  /**
   * @description
   * - 現状graphql-wsでは動作せず、subscriptions-transport-wsを使う必要がある
   * - SSR時にWebSocketLinkを生成しようとするとエラーになるのでハンドリングする。
   * https://qiita.com/mu-suke08/items/6dc353dd641e352f350e
   */
  const wsLink =
    typeof window !== 'undefined'
      ? new WebSocketLink(
          new SubscriptionClient(config.coreApiSubscriptionUrl, {
            lazy: true,
            reconnect: true,
            connectionParams: async () => {
              const idToken = await getAccessToken()
              return { idToken, appLink: config.applicationKey }
            },
          })
        )
      : null

  const splitLink =
    typeof window !== 'undefined' && wsLink != null
      ? split(
          ({ query }) => {
            const definition = getMainDefinition(query)
            return (
              definition.kind === 'OperationDefinition' &&
              definition.operation === 'subscription'
            )
          },
          wsLink,
          httpLink
        )
      : httpLink

  return ApolloLink.from([authLink, splitLink])
}

const createApolloClient = () => {
  /**
   * not use persist cache when server render
   */
  if (typeof window !== 'undefined') {
    persistCacheSync({
      cache,
      storage: new StorageWrapper(Preferences),
    })
  }

  const client = new ApolloClient({
    version: packageJson.version,
    name: packageJson.name,
    link: createLink(),
    cache,
    connectToDevTools: config.environment !== 'production',
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-and-network',
      },
    },
  })

  client.onResetStore(async () => {
    client.setLink(createLink())
  })

  return client
}

function initializeApollo() {
  if (!apolloClient) {
    apolloClient = createApolloClient()
  }

  return apolloClient
}

export function useApolloClient() {
  return useMemo(() => initializeApollo(), [])
}
/* フックを使えない場所から useApolloClient と同じインスタンスを取り出す */
export function getApolloClient() {
  return initializeApollo()
}
export { ApolloProvider, createApolloClient, gql }
